import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { dataEngineApi } from '../../services/dataEngineApi';
import ComparisonTable from './reconcile/ComparisonTable';
import AgingTable from './reconcile/AgingTable';
import BalanceTable from './reconcile/BalanceTable';
import CashFlowTable from './reconcile/CashFlowTable';

const formatMoney = (v) => '$' + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ReconcileTab({ company }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cfExpandedSection, setCfExpandedSection] = useState(null);
  const [mode, setMode] = useState('live');
  const [snapshotResults, setSnapshotResults] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const rfId = company?.rf_id;

  const fetchReconciliation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await dataEngineApi.reconcile(rfId, startDate || null, endDate || null);
      if (resp && resp.data) {
        setData(resp.data);
      } else if (resp && resp.code) {
        setError(resp.message || 'Reconciliation failed');
      } else {
        setError('Unexpected response format');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch reconciliation');
    } finally {
      setLoading(false);
    }
  }, [rfId, startDate, endDate]);

  const loadSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const results = await dataEngineApi.getLatestReconciliationResults(company.rf_id);
      setSnapshotResults(results);
    } catch (err) {
      console.error('Failed to load snapshot results:', err);
    } finally {
      setSnapshotLoading(false);
    }
  };

  useEffect(() => { fetchReconciliation(); }, [fetchReconciliation]);
  useEffect(() => {
    if (mode === 'snapshot') loadSnapshot();
  }, [mode]);

  // Compute live status only when data is available
  const liveStatus = data ? (() => {
    const pnlMatch = data.pnl_results.every(r => r.match);
    const bsMatch = !data.bs_results?.length || data.bs_results.every(r => r.match);
    const arMatch = !data.ar_aging || data.ar_aging.match;
    const apMatch = !data.ap_aging || data.ap_aging.match;
    const custMatch = !data.customer_balance || data.customer_balance.match;
    const vendMatch = !data.vendor_balance || data.vendor_balance.match;
    const cfMatch = !data.cash_flow || data.cash_flow.match;
    const allMatch = pnlMatch && bsMatch && arMatch && apMatch && custMatch && vendMatch && cfMatch;
    const parts = [];
    if (!pnlMatch) parts.push('P&L');
    if (!bsMatch) parts.push('Balance Sheet');
    if (!arMatch) parts.push('AR Aging');
    if (!apMatch) parts.push('AP Aging');
    if (!custMatch) parts.push('Customer Balance');
    if (!vendMatch) parts.push('Vendor Balance');
    if (!cfMatch) parts.push('Cash Flow');
    return { allMatch, parts };
  })() : null;

  return (
    <div className="flex flex-col h-full">
      {/* Mode toggle + date range selector */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-800 bg-gray-800/20">
        {/* Mode toggle */}
        <div className="flex gap-1 mr-2">
          {['live', 'snapshot'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                mode === m
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'text-gray-500 hover:text-gray-300 border border-gray-700'
              }`}
            >
              {m === 'live' ? 'Live' : 'Snapshot'}
            </button>
          ))}
        </div>

        {mode === 'live' && (
          <>
            <span className="text-xs text-gray-500">Date Range:</span>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-gray-500" />
            <span className="text-gray-600">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-gray-500" />
            <button onClick={fetchReconciliation}
              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded hover:bg-blue-500/20 transition-all">
              <RefreshCw className="h-3 w-3" /> Apply
            </button>
            {(startDate || endDate) && (
              <button onClick={() => { setStartDate(''); setEndDate(''); setTimeout(fetchReconciliation, 100); }}
                className="text-xs text-gray-500 hover:text-gray-300">Clear</button>
            )}
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Snapshot mode */}
        {mode === 'snapshot' && (
          <div>
            {snapshotLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
              </div>
            ) : snapshotResults?.length > 0 ? (
              <>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded px-3 py-2 text-xs text-blue-300 mb-3">
                  Compared against QB snapshot from {new Date(snapshotResults[0].created_at).toLocaleString()}
                </div>
                {snapshotResults.filter(r => r.report_type === 'PNL').map(r => {
                  const pnlResults = (r.variance || []).map(v => ({
                    category: v.category,
                    qb_total: v.qb,
                    cube_total: v.cube ?? v.duckdb,
                    variance: v.diff,
                    match: v.match,
                  }));
                  return (
                    <div key={r.id} className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-400">P&L Reconciliation</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${r.status === 'pass' ? 'bg-emerald-400/10 text-emerald-400' : 'bg-red-400/10 text-red-400'}`}>
                          {r.status === 'pass' ? 'Pass' : 'Fail'}
                        </span>
                      </div>
                      <ComparisonTable results={pnlResults} />
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="text-center text-gray-500 text-xs py-8">No snapshot results available. Run a sync first.</div>
            )}
          </div>
        )}

        {/* Live mode */}
        {mode === 'live' && (
          <>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-amber-400 mb-3" />
                <p className="text-sm text-gray-500">Fetching QB reports and comparing with FinBoard...</p>
              </div>
            ) : error ? (
              <div className="bg-red-500/5 border border-red-500/10 rounded-lg px-4 py-3 text-sm text-red-400">{error}</div>
            ) : data ? (
              <>
                {/* Overall status banner */}
                <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                  liveStatus.allMatch ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-amber-500/5 border border-amber-500/10'
                }`}>
                  {liveStatus.allMatch
                    ? <CheckCircle className="h-5 w-5 text-emerald-400" />
                    : <AlertCircle className="h-5 w-5 text-amber-400" />}
                  <div>
                    <div className={`text-sm font-medium ${liveStatus.allMatch ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {liveStatus.allMatch ? 'All reports match' : `Variances detected in ${liveStatus.parts.join(' & ')}`}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {(data.cube_row_count ?? data.duckdb_row_count).toLocaleString()} transactions · {data.start_date} to {data.end_date}
                    </div>
                  </div>
                </div>

                {/* P&L */}
                <ComparisonTable title="Profit & Loss" results={data.pnl_results} />

                {/* P&L Totals */}
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="bg-gray-800/30 border border-gray-800 rounded-lg px-3 py-2">
                    <div className="text-gray-500">QB P&L Total</div>
                    <div className="text-gray-200 font-mono mt-0.5">{formatMoney(data.total_qb)}</div>
                  </div>
                  <div className="bg-gray-800/30 border border-gray-800 rounded-lg px-3 py-2">
                    <div className="text-gray-500">FinBoard P&L Total</div>
                    <div className="text-gray-200 font-mono mt-0.5">{formatMoney(data.total_cube ?? data.total_duckdb)}</div>
                  </div>
                  <div className={`rounded-lg px-3 py-2 border ${
                    Math.abs(data.total_variance) <= 1 ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-red-500/5 border-red-500/10'
                  }`}>
                    <div className="text-gray-500">Variance</div>
                    <div className={`font-mono mt-0.5 ${Math.abs(data.total_variance) <= 1 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {data.total_variance >= 0 ? '+' : ''}{formatMoney(data.total_variance)}
                    </div>
                  </div>
                </div>

                {/* BS */}
                <ComparisonTable title="Balance Sheet" results={data.bs_results || []} />

                {data.bs_results?.length > 0 && data.bs_results.some(r => !r.match) && (
                  <div className="bg-sky-500/5 border border-sky-500/10 rounded-lg px-3 py-2 text-[11px] text-sky-400">
                    BS variance is expected if not syncing full transaction history from company inception.
                  </div>
                )}

                {/* AR / AP Aging */}
                <AgingTable title="Accounts Receivable Aging" aging={data.ar_aging} color="text-blue-400" rfId={data.rf_id} />
                <AgingTable title="Accounts Payable Aging" aging={data.ap_aging} color="text-amber-400" rfId={data.rf_id} />

                {/* Customer / Vendor Balance */}
                <BalanceTable title="Customer Balance Summary" balance={data.customer_balance} color="text-blue-400" counterpartyLabel="Customer" />
                <BalanceTable title="Vendor Balance Summary" balance={data.vendor_balance} color="text-amber-400" counterpartyLabel="Vendor" />

                {/* Cash Flow */}
                {data.cash_flow && (
                  <CashFlowTable
                    cashFlow={data.cash_flow}
                    cfExpandedSection={cfExpandedSection}
                    setCfExpandedSection={setCfExpandedSection}
                  />
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
